import { Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { KeyResult, Ppp } from "@/types";
import type { ObjectiveCoverageRow, KrCoverageRow } from "./okr-overview";
import { OkrProgressBar } from "./OkrProgressBar";
import { CATEGORY_BADGE, CATEGORY_LABEL, okrPct } from "./okrConstants";

interface OkrCoverageViewProps {
  rows: ObjectiveCoverageRow[];
  unlinkedKrs: KeyResult[];
  pppsWithoutKr: Ppp[];
  pppsNoDeliverable: Ppp[];
  isLoading: boolean;
  truncated?: boolean;
}

function KrCoverageRowView({ row }: { row: KrCoverageRow }) {
  const { kr, pppCount, committedDeliverables } = row;
  return (
    <div className="grid grid-cols-[3rem_1fr_2.5rem_8rem_auto] items-center gap-3 py-1.5 pl-8 pr-2 text-sm">
      <span className="font-mono text-[10px] text-muted-foreground">{kr.code}</span>
      <span className="truncate">{kr.name}</span>
      <span className="text-xs text-muted-foreground text-right">{kr.weight != null ? `w${Math.round(kr.weight)}%` : "—"}</span>
      <OkrProgressBar progress={kr.progress} targetProgress={kr.targetProgress} />
      {pppCount === 0 ? (
        <Badge variant="destructive" className="w-fit text-[10px]">No PPP</Badge>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="w-fit cursor-default text-[10px]">
              {pppCount} PPP{pppCount === 1 ? "" : "s"} · {committedDeliverables} via PPPs
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            Deliverables committed through the {pppCount} linked PPP{pppCount === 1 ? "" : "s"}. A deliverable
            linked to 2 PPPs is counted twice.
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

function PppMiniList({ title, ppps, emptyLabel }: { title: string; ppps: Ppp[]; emptyLabel: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="mb-2 text-sm font-semibold">{title} ({ppps.length})</p>
      {ppps.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1">
          {ppps.map((p) => (
            <li key={p.pid} className="truncate text-xs text-muted-foreground">{p.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Read-only coverage audit: which KRs have no PPP behind them, and which PPPs deliver nothing. */
export function OkrCoverageView({ rows, unlinkedKrs, pppsWithoutKr, pppsNoDeliverable, isLoading, truncated }: OkrCoverageViewProps) {
  if (isLoading) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading coverage…</div>;
  }

  return (
    <div className="space-y-4">
      {truncated && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          Only the first 500 rows of each list were loaded — coverage may be incomplete.
        </p>
      )}
      {rows.map(({ objective, krs }) => {
        const pct = okrPct(objective.progress, objective.targetProgress);
        return (
          <div key={objective.pid} className="rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <span className="font-mono text-[10px] text-muted-foreground">{objective.code}</span>
              <span className={cn("rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase", CATEGORY_BADGE[objective.category])}>
                {CATEGORY_LABEL[objective.category]}
              </span>
              <span className="flex-1 truncate text-sm font-semibold">{objective.name}</span>
              <span className="text-xs font-semibold text-muted-foreground">{pct}%</span>
            </div>
            {krs.length === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">No key results — progress stays 0.</p>
            ) : (
              <div className="divide-y divide-border/60">
                {krs.map((row) => <KrCoverageRowView key={row.kr.pid} row={row} />)}
              </div>
            )}
          </div>
        );
      })}

      {unlinkedKrs.length > 0 && (
        <div className="rounded-xl border border-dashed border-border p-4">
          <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
            <Layers className="h-3.5 w-3.5" /> Unlinked key results ({unlinkedKrs.length})
          </p>
          <p className="text-xs text-muted-foreground">
            No objective link yet — excluded from the rollups above until the BE sends it.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PppMiniList title="PPPs without a KR" ppps={pppsWithoutKr} emptyLabel="Every PPP links at least one KR." />
        <PppMiniList
          title="Ranked PPPs with no committed deliverable"
          ppps={pppsNoDeliverable}
          emptyLabel="Every ranked PPP has at least one committed deliverable."
        />
      </div>
    </div>
  );
}
